import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, MessageSquare } from "lucide-react";
import AppLayout, { PageHeader, ComingSoonNote } from "@/components/app/AppLayout";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const auth = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    if (!auth?.user || typeof auth.user !== "object") return;
    const headers = auth.apiHeaders();
    axios.get(`${API}/conversations/seed`, { headers })
      .then(() => axios.get(`${API}/conversations`, { headers }))
      .then((r) => { setConvs(r.data || []); if (r.data?.[0]) setActive(r.data[0]); })
      .catch(() => {});
  }, [auth]);

  useEffect(() => {
    if (!active) return;
    axios.get(`${API}/conversations/${active.id}/messages`, { headers: auth.apiHeaders() })
      .then((r) => { setMessages(r.data || []); setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50); })
      .catch(() => {});
  }, [active, auth]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      const { data } = await axios.post(`${API}/conversations/${active.id}/messages`, { conversation_id: active.id, text: text.trim() }, { headers: auth.apiHeaders() });
      setMessages((m) => [...m, data]);
      setText("");
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {}
  };

  if (!auth?.user || typeof auth.user !== "object") {
    return (
      <AppLayout>
        <PageHeader kicker="◊ Mensajes" title="Mensajes." subtitle="Inicia sesión para chatear con tus vínculos." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader kicker="◊ Mensajes" title="Mensajes." subtitle="Chat directo entre vínculos · texto, en tiempo real cuando estés conectado." />
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-3 border hairline bg-cream-card rounded-2xl overflow-hidden h-[640px]" data-testid="messages-grid">
        <aside className="border-r hairline overflow-y-auto" data-testid="messages-list">
          {convs.length === 0 && <p className="p-6 text-xs text-ink-mid">Sin conversaciones</p>}
          {convs.map((c) => (
            <button
              key={c.id}
              data-testid={`conv-${c.id}`}
              onClick={() => setActive(c)}
              className={`w-full text-left p-3 flex items-center gap-3 hover:bg-cream transition ${active?.id === c.id ? "bg-coral/10" : ""}`}
            >
              <img src={c.other_avatar} alt="" className="w-10 h-10 rounded-full bg-cream-deep" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{c.other_name}</p>
                <p className="text-xs text-ink-mid truncate">{c.last_message}</p>
              </div>
              {c.unread > 0 && <span className="w-5 h-5 text-[10px] font-mono rounded-full bg-coral text-cream flex items-center justify-center">{c.unread}</span>}
            </button>
          ))}
        </aside>

        <section className="flex flex-col">
          {active ? (
            <>
              <header className="px-5 py-3 border-b hairline flex items-center gap-3">
                <img src={active.other_avatar} alt="" className="w-9 h-9 rounded-full bg-cream-deep" />
                <p className="font-medium" data-testid="conv-active-name">{active.other_name}</p>
              </header>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="conv-messages">
                {messages.map((m) => {
                  const isMe = m.sender_id === auth.user.id;
                  return (
                    <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${isMe ? "bg-coral text-cream" : "bg-cream border hairline"}`}>{m.text}</div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t hairline p-3 flex items-center gap-2">
                <input
                  data-testid="conv-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Escribe un mensaje…"
                  className="flex-1 bg-cream border hairline rounded-full px-4 py-2 text-sm outline-none focus:border-coral"
                />
                <button data-testid="conv-send" type="submit" className="w-10 h-10 rounded-full bg-coral text-white flex items-center justify-center hover:bg-coral-hover">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-ink-mid">
              <MessageSquare className="w-5 h-5 mr-2" /> Selecciona una conversación
            </div>
          )}
        </section>
      </div>
      <ComingSoonNote feature="imágenes, archivos adjuntos, llamadas y push notifications" eta="próxima fase" />
    </AppLayout>
  );
}
